/**
 * Friend requests and accepted friendships in Firestore.
 */
import {
  collection,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  onSnapshot,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { deletePrivateConversation, friendshipDocumentId } from '../privateChat';
import { canReceiveFriendRequest } from '../utils/legacyMockUsers';
export const FRIEND_REQUESTS_COLLECTION = 'friendRequests';
export const FRIENDSHIPS_COLLECTION = 'friendships';

/** Machine-readable reason returned by the accept callable when a Free user is at the cap. */
export const FRIEND_LIMIT_REACHED = 'friend_limit_reached';

export const FriendRequestStatus = {
  PENDING: 'PENDING',
  ACCEPTED: 'ACCEPTED',
  DECLINED: 'DECLINED',
};

function mapRequest(id, data) {
  return {
    id,
    ...data,
    createdAt: data.createdAt?.toMillis?.() ?? data.createdAt ?? null,
    updatedAt: data.updatedAt?.toMillis?.() ?? data.updatedAt ?? null,
  };
}

const friendshipDocId = friendshipDocumentId;

function mapFriendFromMembership(myUid, friendship) {
  const otherId = friendship.userIds?.find((id) => id !== myUid);
  if (!otherId) return null;
  const member = friendship.members?.[otherId] || {};
  const blockedBy = friendship.blockedBy || {};
  const blockedByMe = !!blockedBy[myUid];
  /** Other user blocked me — they disappear from my UI; I cannot unblock. */
  const blocksMe = !!blockedBy[otherId];
  return {
    id: otherId,
    name: member.displayName || member.username || 'Player',
    username: member.username,
    avatar: member.avatarUrl || null,
    blockedByMe,
    blocksMe,
    isBlocked: blockedByMe,
    isHidden: blockedByMe || blocksMe,
  };
}

/** True when either side blocked — both players are invisible to each other in the app. */
export function isPlayerHiddenFromMe(friendOrId, friendsList) {
  if (!friendOrId) return false;
  if (typeof friendOrId === 'object') {
    return !!(friendOrId.blockedByMe || friendOrId.blocksMe || friendOrId.isHidden);
  }
  const f = (friendsList || []).find((x) => x.id === friendOrId);
  return !!(f?.blockedByMe || f?.blocksMe);
}

/** @returns {Promise<boolean>} true if a pending request already exists between these users */
export async function hasPendingRequestBetween(uidA, uidB) {
  if (!uidA || !uidB || uidA === uidB) return false;
  const col = collection(db, FRIEND_REQUESTS_COLLECTION);
  const [sent, received] = await Promise.all([
    getDocs(query(col, where('fromUserId', '==', uidA), where('toUserId', '==', uidB), where('status', '==', FriendRequestStatus.PENDING))),
    getDocs(query(col, where('fromUserId', '==', uidB), where('toUserId', '==', uidA), where('status', '==', FriendRequestStatus.PENDING))),
  ]);
  return !sent.empty || !received.empty;
}

export async function areFriends(uidA, uidB) {
  if (!uidA || !uidB) return false;
  try {
    const snap = await getDoc(doc(db, FRIENDSHIPS_COLLECTION, friendshipDocId(uidA, uidB)));
    return snap.exists();
  } catch (err) {
    if (err?.code === 'permission-denied') return false;
    throw err;
  }
}

/**
 * @param {string} fromUserId
 * @param {object} toPlayer — { id, name, username, avatar }
 * @param {object} fromProfile — { displayName, usernameLower, avatarUrl }
 */
export async function sendFriendRequest(fromUserId, toPlayer, fromProfile) {
  if (!fromUserId || !toPlayer?.id) throw new Error('Invalid friend request');
  if (!canReceiveFriendRequest(fromUserId) || !canReceiveFriendRequest(toPlayer.id)) {
    throw new Error('INVALID_FRIEND_REQUEST_TARGET');
  }
  if (fromUserId === toPlayer.id) throw new Error('Cannot add yourself');

  const pending = await hasPendingRequestBetween(fromUserId, toPlayer.id);
  if (pending) throw new Error('REQUEST_ALREADY_PENDING');

  if (await areFriends(fromUserId, toPlayer.id)) throw new Error('ALREADY_FRIENDS');

  await addDoc(collection(db, FRIEND_REQUESTS_COLLECTION), {
    fromUserId,
    toUserId: toPlayer.id,
    fromDisplayName: fromProfile.displayName || fromProfile.usernameLower || 'Player',
    fromUsername: fromProfile.displayName || fromProfile.usernameLower || toPlayer.username || '',
    fromAvatarUrl: fromProfile.avatarUrl || toPlayer.avatar || null,
    toDisplayName: toPlayer.name || null,
    status: FriendRequestStatus.PENDING,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

let acceptFriendRequestFn = null;
function getAcceptFriendRequestFn() {
  if (!acceptFriendRequestFn) {
    acceptFriendRequestFn = httpsCallable(functions, 'accept_friend_request');
  }
  return acceptFriendRequestFn;
}

/**
 * Accept a pending friend request. Server-authoritative: the callable enforces
 * the Free friend cap and creates the friendship doc. Clients can no longer set
 * a request to ACCEPTED or write friendships/{id} directly (see firestore.rules).
 *
 * @param {object|string} request — request object (uses `.id`) or a request id
 * @returns {Promise<{ ok: boolean, friendshipId: string, friendId: string } | null>}
 * @throws on failure; a Free-user-at-cap error has `err.details.reason === FRIEND_LIMIT_REACHED`
 */
export async function acceptFriendRequest(request) {
  const requestId = typeof request === 'string' ? request : request?.id;
  if (!requestId) throw new Error('Invalid request');
  const result = await getAcceptFriendRequestFn()({ requestId });
  return result?.data ?? null;
}

export async function setFriendBlocked(myUid, otherUid, blocked) {
  if (!myUid || !otherUid || myUid === otherUid) return;
  await updateDoc(doc(db, FRIENDSHIPS_COLLECTION, friendshipDocId(myUid, otherUid)), {
    [`blockedBy.${myUid}`]: !!blocked,
  });
}

/**
 * Unblock ends the friendship: private chat is wiped and both must send a new friend request.
 */
export async function unblockAndResetRelationship(myUid, otherUid) {
  if (!myUid || !otherUid || myUid === otherUid) return [];
  const convId = friendshipDocumentId(myUid, otherUid);
  await deletePrivateConversation(convId);
  return removeFriendship(myUid, otherUid);
}

/** Clears any pending requests between two users so re-adding starts fresh. */
async function cancelPendingRequestsBetween(uidA, uidB) {
  if (!uidA || !uidB) return;
  const col = collection(db, FRIEND_REQUESTS_COLLECTION);
  const [sent, received] = await Promise.all([
    getDocs(
      query(
        col,
        where('fromUserId', '==', uidA),
        where('toUserId', '==', uidB),
        where('status', '==', FriendRequestStatus.PENDING)
      )
    ),
    getDocs(
      query(
        col,
        where('fromUserId', '==', uidB),
        where('toUserId', '==', uidA),
        where('status', '==', FriendRequestStatus.PENDING)
      )
    ),
  ]);
  const pending = [...sent.docs, ...received.docs];
  if (pending.length === 0) return [];

  const batch = writeBatch(db);
  pending.forEach((snap) => {
    batch.update(snap.ref, {
      status: FriendRequestStatus.DECLINED,
      updatedAt: serverTimestamp(),
    });
  });
  await batch.commit();
  return pending.map((snap) => snap.id);
}

/** Removes friendship for both users; re-adding requires a new friend request. */
export async function removeFriendship(myUid, otherUid) {
  if (!myUid || !otherUid || myUid === otherUid) return [];
  const clearedRequestIds = await cancelPendingRequestsBetween(myUid, otherUid);
  await deleteDoc(doc(db, FRIENDSHIPS_COLLECTION, friendshipDocId(myUid, otherUid)));
  return clearedRequestIds;
}

/** Sender withdraws a pending friend request before it is accepted. */
export async function cancelOutgoingFriendRequest(requestId, fromUserId) {
  if (!requestId || !fromUserId) throw new Error('Invalid request');
  const ref = doc(db, FRIEND_REQUESTS_COLLECTION, requestId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('REQUEST_NOT_FOUND');
  const data = snap.data();
  if (data.fromUserId !== fromUserId) throw new Error('NOT_REQUEST_SENDER');
  if (data.status !== FriendRequestStatus.PENDING) throw new Error('REQUEST_NOT_PENDING');
  await updateDoc(ref, {
    status: FriendRequestStatus.DECLINED,
    updatedAt: serverTimestamp(),
  });
}

export async function declineFriendRequest(requestId) {
  await updateDoc(doc(db, FRIEND_REQUESTS_COLLECTION, requestId), {
    status: FriendRequestStatus.DECLINED,
    updatedAt: serverTimestamp(),
  });
}

/** Incoming pending requests for this user */
export function subscribeIncomingFriendRequests(userId, onRequests, onError) {
  if (!userId) {
    onRequests([]);
    return () => {};
  }
  const q = query(
    collection(db, FRIEND_REQUESTS_COLLECTION),
    where('toUserId', '==', userId),
    where('status', '==', FriendRequestStatus.PENDING)
  );
  return onSnapshot(
    q,
    (snap) => {
      const list = snap.docs.map((d) => mapRequest(d.id, d.data())).sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
      onRequests(list);
    },
    (err) => {
      console.error('subscribeIncomingFriendRequests:', err);
      onError?.(err);
      onRequests([]);
    }
  );
}

/** Outgoing pending requests from this user */
export function subscribeOutgoingFriendRequests(userId, onRequests) {
  if (!userId) {
    onRequests([]);
    return () => {};
  }
  const q = query(
    collection(db, FRIEND_REQUESTS_COLLECTION),
    where('fromUserId', '==', userId),
    where('status', '==', FriendRequestStatus.PENDING)
  );
  return onSnapshot(
    q,
    (snap) => {
      const list = snap.docs.map((d) => mapRequest(d.id, d.data()));
      onRequests(list);
    },
    (err) => {
      console.error('subscribeOutgoingFriendRequests:', err);
      onRequests([]);
    }
  );
}

/** Friend requests this user sent that were declined (notify sender). */
export function subscribeDeclinedSentFriendRequests(userId, onRequests, onError) {
  if (!userId) {
    onRequests([]);
    return () => {};
  }
  const q = query(
    collection(db, FRIEND_REQUESTS_COLLECTION),
    where('fromUserId', '==', userId),
    where('status', '==', FriendRequestStatus.DECLINED)
  );
  return onSnapshot(
    q,
    (snap) => {
      const list = snap.docs
        .map((d) => mapRequest(d.id, d.data()))
        .sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0));
      onRequests(list);
    },
    (err) => {
      console.error('subscribeDeclinedSentFriendRequests:', err);
      onError?.(err);
      onRequests([]);
    }
  );
}

/** Accepted friends list */
export function subscribeFriendships(userId, onFriends, onError) {
  if (!userId) {
    onFriends([]);
    return () => {};
  }
  const q = query(
    collection(db, FRIENDSHIPS_COLLECTION),
    where('userIds', 'array-contains', userId)
  );
  return onSnapshot(
    q,
    (snap) => {
      const friends = snap.docs
        .map((d) => mapFriendFromMembership(userId, { id: d.id, ...d.data() }))
        .filter(Boolean);
      onFriends(friends);
    },
    (err) => {
      console.error('subscribeFriendships:', err);
      onError?.(err);
      onFriends([]);
    }
  );
}
